import type { Payment, PaymentStatus, PaymentMethod, PaymentMethodType } from '@/lib/types';
import { generateNumericId } from '@/lib/utils';
import { format } from 'date-fns';
import { serviceLogger } from '@/lib/utils/logger';
import { isSampleDataMode } from '@/lib/config/env';

const SAMPLE_DATA_DELAY = 100;
const logger = serviceLogger('PaymentManager');

// In-memory storage for sample data mode - initialize safely
let localPayments: Payment[] = [];
let localPaymentMethods: PaymentMethod[] = [];

// Initialize data safely
const initializeData = async (): Promise<void> => {
  if (localPayments.length === 0 && localPaymentMethods.length === 0) {
    try {
      // Only import sample data if we're in sample data mode
      if (isSampleDataMode()) {
        const sampleDataModule = await import('@/lib/sample-data');
        localPayments = sampleDataModule.samplePaymentsData ? JSON.parse(JSON.stringify(sampleDataModule.samplePaymentsData)) : [];
        localPaymentMethods = sampleDataModule.samplePaymentMethodsData ? JSON.parse(JSON.stringify(sampleDataModule.samplePaymentMethodsData)) : [];
      }
    } catch (importError) {
      console.warn('Sample payment data not available:', importError);
      localPayments = [];
      localPaymentMethods = [];
    }
  }
};

// Initialize on module load
initializeData().catch(error => {
  console.error('Failed to initialize payment data:', error);
  localPayments = [];
  localPaymentMethods = [];
});

export interface PaymentFilters {
  status?: PaymentStatus;
  userId?: string;
  subscriptionId?: string;
  startDate?: string;
  endDate?: string;
}

export interface ProcessPaymentRequest {
  action: 'charge' | 'refund' | 'cancel';
  paymentId?: string;
  amount: number;
  description: string;
  subscriptionId: string;
  userId?: string;
  currencyCode?: string;
  paymentMethodId?: string;
}

export interface ProcessPaymentResponse {
  success: boolean;
  paymentId: string;
  status: PaymentStatus;
  message: string;
  transactionId?: string;
}

/**
 * Simplified Payment Manager
 * Combines payment processing, payment tracking, and payment method management
 */
export const paymentManager = {
  // ===== PAYMENT METHODS =====
  
  /**
   * Get payment methods for a user
   */
  async getPaymentMethodsForUser(userId: string): Promise<PaymentMethod[]> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const userMethods = localPaymentMethods.filter(pm => pm.userUid === userId);
          resolve(JSON.parse(JSON.stringify(userMethods)));
        } catch (error) {
          logger.error('Error getting payment methods for user:', error);
          resolve([]);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Add a new payment method
   */
  async addPaymentMethod(
    userId: string, 
    methodDetails: {
      type: PaymentMethodType;
      last4: string;
      expiryMonth: number;
      expiryYear: number;
    }
  ): Promise<PaymentMethod | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const existingUserMethods = localPaymentMethods.filter(pm => pm.userUid === userId);
          
          const newPaymentMethod: PaymentMethod = {
            id: `pm_${generateNumericId(10)}`,
            userUid: userId,
            type: methodDetails.type,
            last4: methodDetails.last4,
            expiryMonth: methodDetails.expiryMonth,
            expiryYear: methodDetails.expiryYear,
            isDefault: existingUserMethods.length === 0, // First method is default
            provider: 'sample',
            imageUrl: undefined,
          };
          
          localPaymentMethods.push(newPaymentMethod);
          logger.info('Payment method added successfully', { paymentMethodId: newPaymentMethod.id, userId: `***${userId.slice(-4)}` });
          resolve(JSON.parse(JSON.stringify(newPaymentMethod)));
        } catch (error) {
          logger.error('Error adding payment method:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Set default payment method for a user
   */
  async setDefaultPaymentMethod(userId: string, paymentMethodId: string): Promise<PaymentMethod[] | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          let userMethods = localPaymentMethods.filter(pm => pm.userUid === userId);
          let found = false;
          
          // Update user methods: set target as default, others as non-default
          userMethods = userMethods.map(pm => {
            if (pm.id === paymentMethodId) {
              found = true;
              return { ...pm, isDefault: true };
            }
            return { ...pm, isDefault: false };
          });

          if (found) {
            // Update the main store
            localPaymentMethods = localPaymentMethods.map(pm => {
              if (pm.userUid === userId) {
                return userMethods.find(um => um.id === pm.id) || pm;
              }
              return pm;
            });
            
            logger.info('Default payment method set successfully', { userId: `***${userId.slice(-4)}`, paymentMethodId });
            resolve(JSON.parse(JSON.stringify(userMethods)));
          } else {
            logger.warn('Payment method not found for user', { userId: `***${userId.slice(-4)}`, paymentMethodId });
            resolve(null);
          }
        } catch (error) {
          logger.error('Error setting default payment method:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Remove a payment method
   */
  async removePaymentMethod(userId: string, paymentMethodId: string): Promise<PaymentMethod[] | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const initialLength = localPaymentMethods.length;
          localPaymentMethods = localPaymentMethods.filter(
            pm => !(pm.userUid === userId && pm.id === paymentMethodId)
          );
          
          if (localPaymentMethods.length < initialLength) {
            const remainingUserMethods = localPaymentMethods.filter(pm => pm.userUid === userId);
            
            // If removed method was default and there are remaining methods, make first one default
            if (!remainingUserMethods.some(pm => pm.isDefault) && remainingUserMethods.length > 0) {
              remainingUserMethods[0].isDefault = true;
              localPaymentMethods = localPaymentMethods.map(pm => {
                if (pm.id === remainingUserMethods[0].id) return remainingUserMethods[0];
                return pm;
              });
            }
            
            logger.info('Payment method removed successfully', { userId: `***${userId.slice(-4)}`, paymentMethodId });
            resolve(JSON.parse(JSON.stringify(remainingUserMethods)));
          } else {
            logger.warn('Payment method not found or not removed', { userId, paymentMethodId });
            resolve(null);
          }
        } catch (error) {
          logger.error('Error removing payment method:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  // ===== PAYMENTS =====

  /**
   * Get all payments with optional filtering
   */
  async getAllPayments(filters?: PaymentFilters): Promise<Payment[]> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          let payments = JSON.parse(JSON.stringify(localPayments));
          
          // Apply filters
          if (filters) {
            if (filters.userId) {
              payments = payments.filter((p: Payment) => p.userUid === filters.userId);
            }
            if (filters.subscriptionId) {
              payments = payments.filter((p: Payment) => p.subscriptionId === filters.subscriptionId);
            }
            if (filters.status) {
              payments = payments.filter((p: Payment) => p.status === filters.status);
            }
            if (filters.startDate) {
              payments = payments.filter((p: Payment) => p.dateCreated && p.dateCreated >= filters.startDate!);
            }
            if (filters.endDate) {
              payments = payments.filter((p: Payment) => p.dateCreated && p.dateCreated <= filters.endDate!);
            }
          }
          
          resolve(payments);
        } catch (error) {
          logger.error('Error getting all payments:', error);
          resolve([]);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Get payment by ID
   */
  async getPaymentById(paymentId: string): Promise<Payment | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const payment = localPayments.find(p => p.id === paymentId);
          resolve(payment ? JSON.parse(JSON.stringify(payment)) : null);
        } catch (error) {
          logger.error('Error getting payment by ID:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Create a new payment
   */
  async createPayment(paymentData: Omit<Payment, 'id' | 'dateCreated' | 'dateUpdated'>): Promise<Payment> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // Validate required fields
          if (!paymentData.userUid) {
            throw new Error('User ID is required for payment creation');
          }
          if (!paymentData.amount || paymentData.amount <= 0) {
            throw new Error('Valid payment amount is required');
          }
          if (!paymentData.currencyCode) {
            throw new Error('Currency code is required');
          }
          
          const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          const newPayment: Payment = {
            ...paymentData,
            id: generateNumericId(10),
            status: paymentData.status || 'Pending',
            dateCreated: now,
            dateUpdated: now,
          };
          
          localPayments.push(newPayment);
          logger.info('Payment created successfully', { paymentId: newPayment.id, amount: newPayment.amount });
          resolve(JSON.parse(JSON.stringify(newPayment)));
        } catch (error) {
          logger.error('Error creating payment:', error);
          reject(error);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Update payment
   */
  async updatePayment(paymentId: string, updates: Partial<Payment>): Promise<Payment | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const paymentIndex = localPayments.findIndex(p => p.id === paymentId);
          if (paymentIndex !== -1) {
            // Validate critical field updates
            if (updates.amount !== undefined && updates.amount <= 0) {
              throw new Error('Payment amount must be greater than 0');
            }
            
            localPayments[paymentIndex] = {
              ...localPayments[paymentIndex],
              ...updates,
              dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            };
            
            logger.info('Payment updated successfully', { paymentId });
            resolve(JSON.parse(JSON.stringify(localPayments[paymentIndex])));
          } else {
            logger.warn('Payment not found for update', { paymentId });
            resolve(null);
          }
        } catch (error) {
          logger.error('Error updating payment:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Update payment status
   */
  async updatePaymentStatus(paymentId: string, status: PaymentStatus): Promise<Payment | null> {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          const paymentIndex = localPayments.findIndex(p => p.id === paymentId);
          if (paymentIndex !== -1) {
            const currentStatus = localPayments[paymentIndex].status;
            
            // Validate status transition
            if (currentStatus === 'Paid' && status !== 'Refunded') {
              logger.warn('Invalid status transition attempted', { from: currentStatus, to: status });
            }
            
            localPayments[paymentIndex] = {
              ...localPayments[paymentIndex],
              status,
              dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            };
            
            logger.info('Payment status updated', { paymentId, oldStatus: currentStatus, newStatus: status });
            resolve(JSON.parse(JSON.stringify(localPayments[paymentIndex])));
          } else {
            logger.warn('Payment not found for status update', { paymentId });
            resolve(null);
          }
        } catch (error) {
          logger.error('Error updating payment status:', error);
          resolve(null);
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  /**
   * Process payment (charge, refund, cancel)
   */
  async processPayment(request: ProcessPaymentRequest): Promise<ProcessPaymentResponse> {
    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          let payment: Payment | null = null;
          
          switch (request.action) {
            case 'charge':
              // Create new payment
                             payment = await this.createPayment({
                 amount: request.amount,
                 description: request.description,
                 userUid: request.userId || 'unknown',
                 subscriptionId: request.subscriptionId,
                 currencyCode: request.currencyCode || 'CHF',
                 status: 'Paid',
                 paymentMethodId: request.paymentMethodId,
                 date: format(new Date(), 'yyyy-MM-dd'),
                 tenantId: '',
                 organizationId: '',
               });
              break;
              
            case 'refund':
              if (!request.paymentId) {
                throw new Error('Payment ID is required for refund');
              }
              payment = await this.updatePaymentStatus(request.paymentId, 'Refunded');
              break;
              
            case 'cancel':
              if (!request.paymentId) {
                throw new Error('Payment ID is required for cancel');
              }
              payment = await this.updatePaymentStatus(request.paymentId, 'Failed');
              break;
              
            default:
              throw new Error(`Unsupported payment action: ${request.action}`);
          }
          
          if (payment) {
            resolve({
              success: true,
              paymentId: payment.id,
              status: payment.status,
              message: `Payment ${request.action} successful`,
              transactionId: `txn_${generateNumericId(10)}`,
            });
          } else {
            throw new Error(`Failed to ${request.action} payment`);
          }
        } catch (error) {
          logger.error('Error processing payment:', error);
          resolve({
            success: false,
            paymentId: request.paymentId || '',
            status: 'Failed' as PaymentStatus,
            message: error instanceof Error ? error.message : 'Payment processing failed',
          });
        }
      }, SAMPLE_DATA_DELAY);
    });
  },

  // ===== CONVENIENCE METHODS =====

  /**
   * Get payments by user ID
   */
  async getPaymentsByUserId(userId: string): Promise<Payment[]> {
    return this.getAllPayments({ userId });
  },

  /**
   * Get payments by subscription ID
   */
  async getPaymentsBySubscriptionId(subscriptionId: string): Promise<Payment[]> {
    return this.getAllPayments({ subscriptionId });
  },

  /**
   * Get payments by status
   */
  async getPaymentsByStatus(status: PaymentStatus): Promise<Payment[]> {
    return this.getAllPayments({ status });
  },

  /**
   * Get recent payments for a user
   */
  async getRecentPaymentsByUserId(userId: string, limit: number = 5): Promise<Payment[]> {
    const userPayments = await this.getPaymentsByUserId(userId);
         return userPayments
       .sort((a, b) => new Date(b.dateCreated || 0).getTime() - new Date(a.dateCreated || 0).getTime())
       .slice(0, limit);
  },

  /**
   * Get total payments for a user
   */
  async getTotalPaymentsByUserId(userId: string): Promise<{ total: number; currencyCode: string }> {
    const userPayments = await this.getPaymentsByUserId(userId);
    const paidPayments = userPayments.filter(p => p.status === 'Paid');
    
    const total = paidPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const currencyCode = paidPayments.length > 0 ? (paidPayments[0].currencyCode || 'CHF') : 'CHF';
    
    return { total, currencyCode };
  },

  /**
   * Search payments by query
   */
  async searchPayments(query: string): Promise<Payment[]> {
    const allPayments = await this.getAllPayments();
    const searchTerm = query.toLowerCase();
    
         return allPayments.filter(payment =>
       payment.description?.toLowerCase().includes(searchTerm) ||
       payment.id.toLowerCase().includes(searchTerm) ||
       payment.subscriptionId?.toLowerCase().includes(searchTerm)
     );
  }
};

export default paymentManager; 
